import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
} from 'react'
import { Button, Card, CardContent, cn } from '@saas-pages/shared/ui'
import {
  startToolOperation,
  trackToolRejection,
  type ToolAnalyticsContext,
  type ToolErrorCode,
  type ToolOperation,
} from '@saas-pages/shared/public/runtime'
import { LocalizedProcessingBadge, useLocale, useT } from '@/shared/i18n'
import {
  downloadPaintBlob,
  encodePaintCanvas,
  PaintExportError,
} from '@/features/online-paint/model/export'
import {
  allPaintCommands,
  appendStrokePoint,
  canvasPointFromClient,
  commitPaintCommand,
  createPaintHistory,
  redoPaintCommand,
  renderPaintScene,
  undoPaintCommand,
  type PaintCommand,
  type PaintExportFormat,
  type PaintHistory,
  type PaintPoint,
  type PaintTool,
} from '@/features/online-paint/model/paint'

const UTILITY_KEY = 'online-paint'
const ANALYTICS_CONTEXT = {
  app: 'imgify',
  domain: 'imgify.ru',
  route: '/risovat-online',
  utility: UTILITY_KEY,
  processingMode: 'local',
} satisfies ToolAnalyticsContext
const CANVAS_PRESETS = [
  { key: 'default', width: 800, height: 600 },
  { key: 'square', width: 1080, height: 1080 },
  { key: 'wide', width: 1280, height: 720 },
  { key: 'a4', width: 1240, height: 1754 },
] as const

interface PointerDraft {
  pointerId: number
  tool: Exclude<PaintTool, 'text'>
  start: PaintPoint
  points: readonly PaintPoint[]
  current: PaintPoint
  startedAt: number
  operation: ToolOperation
}

interface ExportOwner {
  generation: number
  startedAt: number
  operation: ToolOperation
}

const TOOL_LABEL_KEYS: Record<PaintTool, keyof ReturnType<typeof useT>['onlinePaint']['tools']> = {
  brush: 'brush',
  eraser: 'eraser',
  line: 'line',
  rectangle: 'rectangle',
  ellipse: 'ellipse',
  arrow: 'arrow',
  text: 'text',
}

export function OnlinePaintTool() {
  const t = useT().onlinePaint
  const { locale } = useLocale()
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [presetKey, setPresetKey] = useState<(typeof CANVAS_PRESETS)[number]['key']>('default')
  const preset = CANVAS_PRESETS.find((item) => item.key === presetKey) ?? CANVAS_PRESETS[0]
  const [tool, setTool] = useState<PaintTool>('brush')
  const [color, setColor] = useState('#172554')
  const [background, setBackground] = useState('#ffffff')
  const [strokeWidth, setStrokeWidth] = useState(8)
  const [textSize, setTextSize] = useState(48)
  const [textValue, setTextValue] = useState('')
  const [history, setHistory] = useState<PaintHistory>(createPaintHistory)
  const [draft, setDraft] = useState<PaintCommand | null>(null)
  const [message, setMessage] = useState<string | null>(null)
  const [exporting, setExporting] = useState(false)
  const [canvasAvailable, setCanvasAvailable] = useState(true)
  const pointerDraftRef = useRef<PointerDraft | null>(null)
  const historyRef = useRef(history)
  const generationRef = useRef(0)
  const lifecycleGenerationRef = useRef(0)
  const exportOwnerRef = useRef<ExportOwner | null>(null)

  const commands = useMemo(
    () => draft ? [...allPaintCommands(history), draft] : allPaintCommands(history),
    [draft, history],
  )

  const publishHistory = useCallback((next: PaintHistory) => {
    historyRef.current = next
    setHistory(next)
  }, [])

  const redraw = useCallback(() => {
    const canvas = canvasRef.current
    const context = canvas ? getCanvasContext(canvas) : null
    if (!canvas || !context) {
      if (canvas) {
        setCanvasAvailable(false)
        setMessage(t.errors.canvas)
      }
      return
    }
    setCanvasAvailable(true)
    renderPaintScene(context, preset.width, preset.height, background, commands)
  }, [background, commands, preset.height, preset.width, t.errors.canvas])

  useEffect(() => {
    redraw()
  }, [redraw])

  const releasePointerCapture = useCallback((pointerId: number) => {
    const canvas = canvasRef.current
    try {
      if (canvas?.hasPointerCapture(pointerId)) canvas.releasePointerCapture(pointerId)
    } catch {
      // Capture may already be gone after DOM or browser lifecycle changes.
    }
  }, [])

  const cancelPointerOperation = useCallback((pointerId?: number) => {
    const active = pointerDraftRef.current
    if (!active || (pointerId !== undefined && active.pointerId !== pointerId)) return false
    pointerDraftRef.current = null
    setDraft(null)
    active.operation.error('cancelled')
    releasePointerCapture(active.pointerId)
    return true
  }, [releasePointerCapture])

  const failPointerOperation = useCallback((errorCode: ToolErrorCode, errorMessage: string) => {
    const active = pointerDraftRef.current
    if (!active) return
    pointerDraftRef.current = null
    setDraft(null)
    setMessage(errorMessage)
    active.operation.error(errorCode)
    releasePointerCapture(active.pointerId)
  }, [releasePointerCapture])

  const invalidateExport = useCallback(() => {
    generationRef.current += 1
    const owner = exportOwnerRef.current
    owner?.operation.error('cancelled')
    exportOwnerRef.current = null
    setExporting(false)
  }, [])

  useEffect(() => {
    const mountedCanvas = canvasRef.current
    const lifecycleGeneration = ++lifecycleGenerationRef.current
    return () => {
      cancelPointerOperation()
      invalidateExport()
      queueMicrotask(() => {
        if (lifecycleGenerationRef.current !== lifecycleGeneration || !mountedCanvas) return
        mountedCanvas.width = 0
        mountedCanvas.height = 0
      })
    }
  }, [cancelPointerOperation, invalidateExport])

  const clearCanvas = useCallback(() => {
    cancelPointerOperation()
    invalidateExport()
    publishHistory(createPaintHistory())
    setMessage(null)
  }, [cancelPointerOperation, invalidateExport, publishHistory])

  const selectPreset = (next: (typeof CANVAS_PRESETS)[number]['key']) => {
    cancelPointerOperation()
    invalidateExport()
    setPresetKey(next)
    publishHistory(createPaintHistory())
    setMessage(null)
  }

  const commit = useCallback((command: PaintCommand, startedAt: number, operation: ToolOperation) => {
    const result = commitPaintCommand(historyRef.current, command)
    if (!result.ok) {
      setMessage(result.reason === 'point_limit' ? t.errors.pointLimit : t.errors.commandLimit)
      operation.error('size_limit')
      return
    }
    publishHistory(result.history)
    setMessage(null)
    operation.success(performance.now() - startedAt)
  }, [publishHistory, t.errors.commandLimit, t.errors.pointLimit])

  const pointForEvent = (event: ReactPointerEvent<HTMLCanvasElement>): PaintPoint => {
    const canvas = event.currentTarget
    return canvasPointFromClient(
      event.clientX,
      event.clientY,
      canvas.getBoundingClientRect(),
      canvas.width,
      canvas.height,
    )
  }

  const onPointerDown = (event: ReactPointerEvent<HTMLCanvasElement>) => {
    if (event.button !== 0 || pointerDraftRef.current) return
    if (!getCanvasContext(event.currentTarget)) {
      setCanvasAvailable(false)
      setMessage(t.errors.canvas)
      trackToolRejection(ANALYTICS_CONTEXT, 'processing_failed')
      return
    }
    const point = pointForEvent(event)
    const startedAt = performance.now()
    if (tool === 'text') {
      const normalized = textValue.trim().slice(0, 200)
      if (!normalized) {
        setMessage(t.errors.textRequired)
        trackToolRejection(ANALYTICS_CONTEXT, 'invalid_input')
        return
      }
      const operation = startToolOperation(ANALYTICS_CONTEXT)
      commit({ kind: 'text', point, text: normalized, color, size: textSize }, startedAt, operation)
      return
    }

    try {
      event.currentTarget.setPointerCapture(event.pointerId)
    } catch {
      setMessage(t.errors.canvas)
      trackToolRejection(ANALYTICS_CONTEXT, 'processing_failed')
      return
    }
    const operation = startToolOperation(ANALYTICS_CONTEXT)
    const active: PointerDraft = {
      pointerId: event.pointerId,
      tool,
      start: point,
      points: [point],
      current: point,
      startedAt,
      operation,
    }
    pointerDraftRef.current = active
    setDraft(commandFromDraft(active, color, strokeWidth))
  }

  const onPointerMove = (event: ReactPointerEvent<HTMLCanvasElement>) => {
    const active = pointerDraftRef.current
    if (!active || active.pointerId !== event.pointerId) return
    const point = pointForEvent(event)
    active.current = point
    if (active.tool === 'brush' || active.tool === 'eraser') {
      const appended = appendStrokePoint(active.points, point)
      if (!appended.ok) {
        failPointerOperation('size_limit', t.errors.pointLimit)
        return
      }
      active.points = appended.points
    }
    setDraft(commandFromDraft(active, color, strokeWidth))
  }

  const finishPointer = (event: ReactPointerEvent<HTMLCanvasElement>, cancelled: boolean) => {
    const active = pointerDraftRef.current
    if (!active || active.pointerId !== event.pointerId) return
    if (cancelled) {
      cancelPointerOperation(event.pointerId)
      return
    }
    active.current = pointForEvent(event)
    if (active.tool === 'brush' || active.tool === 'eraser') {
      const appended = appendStrokePoint(active.points, active.current)
      if (!appended.ok) {
        failPointerOperation('size_limit', t.errors.pointLimit)
        return
      }
      active.points = appended.points
    }
    pointerDraftRef.current = null
    setDraft(null)
    releasePointerCapture(active.pointerId)
    commit(commandFromDraft(active, color, strokeWidth), active.startedAt, active.operation)
  }

  const undo = useCallback(() => {
    cancelPointerOperation()
    invalidateExport()
    publishHistory(undoPaintCommand(historyRef.current))
    setMessage(null)
  }, [cancelPointerOperation, invalidateExport, publishHistory])

  const redo = useCallback(() => {
    cancelPointerOperation()
    invalidateExport()
    publishHistory(redoPaintCommand(historyRef.current))
    setMessage(null)
  }, [cancelPointerOperation, invalidateExport, publishHistory])

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (!(event.ctrlKey || event.metaKey) || event.key.toLowerCase() !== 'z') return
      const target = event.target
      if (target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement) return
      event.preventDefault()
      if (event.shiftKey) redo()
      else undo()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [redo, undo])

  const exportCanvas = async (format: PaintExportFormat) => {
    if (exporting) return
    const canvas = canvasRef.current
    if (!canvas || !getCanvasContext(canvas)) {
      setCanvasAvailable(false)
      setMessage(t.errors.canvas)
      trackToolRejection(ANALYTICS_CONTEXT, 'processing_failed')
      return
    }
    invalidateExport()
    const owner: ExportOwner = {
      generation: generationRef.current,
      startedAt: performance.now(),
      operation: startToolOperation(ANALYTICS_CONTEXT),
    }
    exportOwnerRef.current = owner
    setExporting(true)
    setMessage(null)
    try {
      const blob = await encodePaintCanvas(canvas, format)
      if (exportOwnerRef.current !== owner || generationRef.current !== owner.generation) return
      downloadPaintBlob(blob, `risunok.${extensionFor(format)}`)
      owner.operation.success(performance.now() - owner.startedAt)
    } catch (error) {
      if (exportOwnerRef.current !== owner || generationRef.current !== owner.generation) return
      const unsupported = error instanceof PaintExportError && error.code === 'unsupported_format'
      setMessage(unsupported ? t.errors.unsupportedFormat : t.errors.export)
      owner.operation.error(unsupported ? 'unsupported_output' : 'processing_failed')
    } finally {
      if (exportOwnerRef.current === owner) {
        exportOwnerRef.current = null
        setExporting(false)
      }
    }
  }

  return (
    <div className="mx-auto max-w-7xl">
      <section className="text-center">
        <div className="mb-3 inline-flex items-center gap-2 rounded-md border bg-card px-3 py-1.5 text-sm text-muted-foreground shadow-sm">
          <LocalizedProcessingBadge variant="local" />
          <span>{t.eyebrow}</span>
        </div>
        <h1 className="text-3xl font-bold tracking-normal sm:text-4xl">{t.title}</h1>
        <p className="mx-auto mt-4 max-w-3xl leading-7 text-muted-foreground">{t.description}</p>
      </section>

      <Card className="mt-8 overflow-hidden">
        <CardContent className="p-3 sm:p-5">
          <div className="grid grid-cols-1 gap-4 xl:grid-cols-[17rem_minmax(0,1fr)]">
            <aside className="min-w-0 space-y-5" aria-label={t.toolbar}>
              <fieldset>
                <legend className="mb-2 text-sm font-semibold">{t.canvasSize}</legend>
                <select
                  aria-label={t.canvasSize}
                  value={presetKey}
                  onChange={(event) => selectPreset(event.target.value as typeof presetKey)}
                  className="h-11 w-full rounded-md border bg-background px-3 text-sm"
                >
                  {CANVAS_PRESETS.map((item) => (
                    <option key={item.key} value={item.key}>
                      {t.presets[item.key]} — {item.width}×{item.height}
                    </option>
                  ))}
                </select>
              </fieldset>

              <fieldset>
                <legend className="mb-2 text-sm font-semibold">{t.tool}</legend>
                <div className="grid grid-cols-2 gap-2">
                  {(Object.keys(TOOL_LABEL_KEYS) as PaintTool[]).map((item) => (
                    <Button
                      key={item}
                      type="button"
                      variant={tool === item ? 'default' : 'outline'}
                      className="min-h-11 justify-start"
                      aria-pressed={tool === item}
                      disabled={!canvasAvailable}
                      onClick={() => setTool(item)}
                    >
                      {t.tools[TOOL_LABEL_KEYS[item]]}
                    </Button>
                  ))}
                </div>
              </fieldset>

              <div className="grid grid-cols-2 gap-3">
                <label className="text-sm font-medium">
                  {t.color}
                  <input
                    aria-label={t.color}
                    type="color"
                    value={color}
                    onChange={(event) => setColor(event.target.value)}
                    className="mt-1 h-11 w-full rounded-md border bg-background p-1"
                  />
                </label>
                <label className="text-sm font-medium">
                  {t.background}
                  <input
                    aria-label={t.background}
                    type="color"
                    value={background}
                    onChange={(event) => {
                      invalidateExport()
                      setBackground(event.target.value)
                    }}
                    className="mt-1 h-11 w-full rounded-md border bg-background p-1"
                  />
                </label>
              </div>

              <label className="block text-sm font-medium">
                {t.strokeWidth}: {strokeWidth}px
                <input
                  aria-label={t.strokeWidth}
                  type="range"
                  min="1"
                  max="64"
                  value={strokeWidth}
                  onChange={(event) => setStrokeWidth(Number(event.target.value))}
                  className="mt-2 w-full"
                />
              </label>

              {tool === 'text' && (
                <div className="space-y-3">
                  <label className="block text-sm font-medium">
                    {t.textValue}
                    <input
                      value={textValue}
                      maxLength={200}
                      onChange={(event) => setTextValue(event.target.value)}
                      className="mt-1 h-11 w-full rounded-md border bg-background px-3"
                    />
                  </label>
                  <label className="block text-sm font-medium">
                    {t.textSize}: {textSize}px
                    <input
                      aria-label={t.textSize}
                      type="range"
                      min="12"
                      max="160"
                      value={textSize}
                      onChange={(event) => setTextSize(Number(event.target.value))}
                      className="mt-2 w-full"
                    />
                  </label>
                </div>
              )}

              <div className="grid grid-cols-2 gap-2">
                <Button type="button" variant="outline" className="min-h-11" disabled={history.undoable.length === 0} onClick={undo}>
                  {t.undo}
                </Button>
                <Button type="button" variant="outline" className="min-h-11" disabled={history.redoable.length === 0} onClick={redo}>
                  {t.redo}
                </Button>
                <Button type="button" variant="outline" className="col-span-2 min-h-11" onClick={clearCanvas}>
                  {t.clear}
                </Button>
              </div>
            </aside>

            <div className="min-w-0">
              <div className="mb-2 flex flex-wrap items-center justify-between gap-2 text-sm text-muted-foreground">
                <span>{preset.width}×{preset.height}px · {t.opaque}</span>
                <span>{t.commandCount}: {history.locked.length + history.undoable.length}/500</span>
              </div>
              <div className="flex max-h-[70vh] min-h-72 items-center justify-center overflow-auto rounded-lg border bg-muted/40 p-2 sm:p-4">
                <canvas
                  ref={canvasRef}
                  width={preset.width}
                  height={preset.height}
                  aria-label={locale === 'ru' ? 'Холст для рисования' : 'Drawing canvas'}
                  role="img"
                  data-testid="online-paint-canvas"
                  aria-disabled={!canvasAvailable}
                  className={cn(
                    'block h-auto max-h-[65vh] max-w-full touch-none rounded-sm border bg-white shadow-sm',
                    tool === 'text' ? 'cursor-text' : 'cursor-crosshair',
                  )}
                  style={{ aspectRatio: `${preset.width} / ${preset.height}` }}
                  onPointerDown={onPointerDown}
                  onPointerMove={onPointerMove}
                  onPointerUp={(event) => finishPointer(event, false)}
                  onPointerCancel={(event) => finishPointer(event, true)}
                  onLostPointerCapture={(event) => cancelPointerOperation(event.pointerId)}
                />
              </div>

              {message && (
                <div role="alert" className="mt-3 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                  {message}
                </div>
              )}

              <div className="mt-4 flex flex-wrap items-center gap-2">
                <span className="mr-1 text-sm font-semibold">{t.download}</span>
                {(['image/png', 'image/jpeg', 'image/webp'] as PaintExportFormat[]).map((format) => (
                  <Button
                    key={format}
                    type="button"
                    variant={format === 'image/png' ? 'default' : 'outline'}
                    className="min-h-11"
                    disabled={exporting || !canvasAvailable}
                    onClick={() => void exportCanvas(format)}
                  >
                    {mimeLabel(format)}
                  </Button>
                ))}
                {exporting && <span className="text-sm text-muted-foreground">{t.exporting}</span>}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <p className="mt-4 text-center text-sm text-muted-foreground">{t.privacy}</p>

      <section className="mx-auto mt-10 grid max-w-5xl gap-6 md:grid-cols-2">
        <div>
          <h2 className="text-xl font-semibold">{t.howToTitle}</h2>
          <ol className="mt-4 space-y-3">
            {t.howTo.map((step, index) => (
              <li key={step} className="flex gap-3 text-sm leading-6 text-muted-foreground">
                <span className="font-semibold text-primary">{index + 1}.</span>{step}
              </li>
            ))}
          </ol>
        </div>
        <div>
          <h2 className="text-xl font-semibold">{t.faqTitle}</h2>
          <div className="mt-4 space-y-3">
            {t.faq.map((item) => (
              <details key={item.question} className="rounded-lg border bg-card px-4 py-3">
                <summary className="cursor-pointer font-medium">{item.question}</summary>
                <p className="mt-2 text-sm leading-6 text-muted-foreground">{item.answer}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto mt-10 max-w-5xl border-t pt-8">
        <h2 className="text-xl font-semibold">{t.relatedTitle}</h2>
        <div className="mt-4 grid gap-3 sm:grid-cols-3">
          {t.related.map((item) => (
            <a key={item.href} href={item.href} className="rounded-lg border bg-card p-4 font-medium shadow-sm hover:border-foreground/30">
              {item.label}
            </a>
          ))}
        </div>
      </section>
    </div>
  )
}

function commandFromDraft(draft: PointerDraft, color: string, width: number): PaintCommand {
  if (draft.tool === 'brush' || draft.tool === 'eraser') {
    return { kind: 'stroke', points: draft.points, erase: draft.tool === 'eraser', color, width }
  }
  return { kind: draft.tool, start: draft.start, end: draft.current, color, width }
}

function getCanvasContext(canvas: HTMLCanvasElement): CanvasRenderingContext2D | null {
  try {
    return canvas.getContext('2d')
  } catch {
    return null
  }
}

function mimeLabel(format: PaintExportFormat): 'PNG' | 'JPG' | 'WebP' {
  if (format === 'image/png') return 'PNG'
  return format === 'image/jpeg' ? 'JPG' : 'WebP'
}

function extensionFor(format: PaintExportFormat): 'png' | 'jpg' | 'webp' {
  if (format === 'image/png') return 'png'
  return format === 'image/jpeg' ? 'jpg' : 'webp'
}
